import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken } from '@morpho-org/dapp-core';
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import { type AvatarSize, DynamicAvatarsStack, type DynamicAvatarsStackAlign, getAvatarSize } from '@morpho-org/ui-v6/client';
import { Icon, useHasTouchInput } from '@repo/ui';
import { Toggletip, ToggletipContent, ToggletipTrigger } from '@repo/ui';
import { Info } from '@repo/ui/icons';
import { useState } from 'react';
import { HoverCard, HoverCardContent as HoverCardContentPrimitive, HoverCardTrigger, Popover, PopoverContent, PopoverTrigger } from '@/components/common/overlays';
import HoverCardContent from './HoverCardContent';
export interface CollateralExposure {
  token: InterfaceToken;
  /** `null` when the collateral has no USD price. */
  supplyAssetsUsd: bigint | null;
  maxTokensCount?: number;
  isIdle?: boolean;
}
export interface CollateralExposureProps extends PropsWithStyles {
  collateralExposures: CollateralExposure[];
  align?: DynamicAvatarsStackAlign;
  maxTokensCount?: number;
  size?: AvatarSize;
  isMobileSupportEnabled?: boolean;
  totalAssetsUsd: bigint | null | undefined;
}
const CollateralExposureStack = t0 => {
  const $ = _c(39);
  const {
    className,
    collateralExposures,
    align: t1,
    maxTokensCount,
    size,
    isMobileSupportEnabled: t2,
    totalAssetsUsd
  } = t0;
  const align = t1 === undefined ? "left" : t1;
  const isMobileSupportEnabled = t2 === undefined ? true : t2;
  const [isOpen, setIsOpen] = useState(false);
  const hasTouchInput = useHasTouchInput();
  let t3;
  if ($[0] !== collateralExposures) {
    t3 = collateralExposures.filter(_temp).map(_temp2);
    $[0] = collateralExposures;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const stackItems = t3;
  if (stackItems.length === 0) {
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Info />;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== size) {
      t5 = size ? {
        width: getAvatarSize(size),
        height: getAvatarSize(size)
      } : undefined;
      $[3] = size;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== t5) {
      t6 = <ToggletipTrigger asChild={true}><Icon role="button" tabIndex={0} aria-label="More information" icon={t4} className="-m-0.5 box-content p-0.5" style={t5} onClick={_temp3} /></ToggletipTrigger>;
      $[5] = t5;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ToggletipContent>This vault is not exposed to any market</ToggletipContent>;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    let t8;
    if ($[8] !== t6) {
      t8 = <Toggletip>{t6}{t7}</Toggletip>;
      $[8] = t6;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    return t8;
  }
  let t4;
  if ($[10] !== collateralExposures || $[11] !== totalAssetsUsd) {
    t4 = collateralExposures.length === 0 ? null : <HoverCardContent collateralExposures={collateralExposures} totalAssetsUsd={totalAssetsUsd} />;
    $[10] = collateralExposures;
    $[11] = totalAssetsUsd;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const hoverCardContent = t4;
  let t5;
  if ($[13] !== align || $[14] !== className || $[15] !== maxTokensCount || $[16] !== size || $[17] !== stackItems) {
    t5 = <DynamicAvatarsStack className={className} items={stackItems} align={align} maxTokensCount={maxTokensCount} avatarSize={size} isMobileSupportEnabled={false} withHoverAnimation={false} />;
    $[13] = align;
    $[14] = className;
    $[15] = maxTokensCount;
    $[16] = size;
    $[17] = stackItems;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const stackComponent = t5;
  if (isMobileSupportEnabled && hasTouchInput) {
    let t6;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = e_0 => {
        e_0.preventDefault();
        e_0.stopPropagation();
        setIsOpen(_temp4);
      };
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    let t7;
    if ($[20] !== stackComponent) {
      t7 = <PopoverTrigger asChild={true}><div onClick={t6}>{stackComponent}</div></PopoverTrigger>;
      $[20] = stackComponent;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        padding: 0,
        outline: "none"
      };
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== hoverCardContent) {
      t9 = <PopoverContent side="top" sideOffset={8} style={t8} onClick={_temp5}>{hoverCardContent}</PopoverContent>;
      $[23] = hoverCardContent;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== isOpen || $[26] !== t7 || $[27] !== t9) {
      t10 = <Popover open={isOpen} onOpenChange={setIsOpen}>{t7}{t9}</Popover>;
      $[25] = isOpen;
      $[26] = t7;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    return t10;
  }
  let t6;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      width: "fit-content"
    };
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== stackComponent) {
    t7 = <HoverCardTrigger asChild={true}><div style={t6}>{stackComponent}</div></HoverCardTrigger>;
    $[30] = stackComponent;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      minWidth: "275px",
      maxWidth: "315px"
    };
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== hoverCardContent) {
    t9 = <HoverCardContentPrimitive style={t8} side="top" sideOffset={8} onClick={_temp6}>{hoverCardContent}</HoverCardContentPrimitive>;
    $[33] = hoverCardContent;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== isOpen || $[36] !== t7 || $[37] !== t9) {
    t10 = <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={200} closeDelay={100}>{t7}{t9}</HoverCard>;
    $[35] = isOpen;
    $[36] = t7;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
};
export default CollateralExposureStack;
function _temp(exposure) {
  return !exposure.isIdle;
}
function _temp2(exposure_0) {
  return {
    logoUrl: exposure_0.token.logoURI
  };
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp4(prev) {
  return !prev;
}
function _temp5(e_1) {
  return e_1.stopPropagation();
}
function _temp6(e_2) {
  return e_2.stopPropagation();
}